import React from "react";
import { Editor as EditorRef } from "@tinymce/tinymce-react";
import { SSMLTag, SSMLTagsColors } from "shared/constants";
import { handleSSMLValueClick } from "shared/utils/tts-editor-helper";

export const handleCharacterUnitSelection = (
  selectedOption: string,
  selectedLanguage: string,
  editorRef: React.RefObject<EditorRef | null>
): void => {
  // Only include language if not 'automatic'
  const values =
    selectedLanguage === "automatic"
      ? `${selectedOption}`
      : `${selectedOption}, ${selectedLanguage}`;

  handleSSMLValueClick(
    editorRef,
    SSMLTag.SayAs,
    values,
    SSMLTagsColors.SayAsTag,
    [SSMLTag.SayAs]
  );
};

export const handleSpellOutSelection = (
  selectedOption: string,
  selectedContent: string,
  selectedLanguage: string,
  editorRef: React.RefObject<EditorRef | null>
): boolean => {
  const regex = /["|]/;

  if (selectedContent === "" || !regex.test(selectedContent)) {
    const values =
      selectedLanguage === "automatic"
        ? `${selectedOption}`
        : `${selectedOption}, ${selectedLanguage}`;

    handleSSMLValueClick(
      editorRef,
      SSMLTag.SayAs,
      values,
      SSMLTagsColors.SayAsTag,
      [SSMLTag.SayAs]
    );
    return true;
  }
  return false;
};

export const handleFractionsSelection = (
  selectedContent: string,
  selectedLanguage: string,
  editorRef: React.RefObject<EditorRef | null>
): boolean => {
  // Accepts both simple fractions (1/2) and mixed fractions (1 1/2)
  const regex = /^\d+\/\d+$|^\d+\s+\d+\/\d+$/;

  if (selectedContent === "" || regex.test(selectedContent)) {
    const values =
      selectedLanguage === "automatic"
        ? `fraction`
        : `fraction, ${selectedLanguage}`;

    handleSSMLValueClick(
      editorRef,
      SSMLTag.SayAs,
      values,
      SSMLTagsColors.SayAsTag,
      [SSMLTag.SayAs]
    );
    return true;
  }
  return false;
};

export const handleDateSelection = (
  selectedContent: string,
  outputFormat: string,
  selectedLanguage: string,
  editorRef: React.RefObject<EditorRef | null>
): boolean => {
  selectedContent = selectedContent.trim();

  // Parse the input to determine its structure
  const hasDelimiters = /[./-]/.test(selectedContent);
  const parts = hasDelimiters ? selectedContent.split(/[./-]/) : null;
  const partCount = parts ? parts.length : 0;

  // Validate based on the chosen format
  let isValidForFormat = false;

  switch (outputFormat.toLowerCase()) {
    case "y":
      isValidForFormat = /^\d{4}$/.test(selectedContent);
      break;

    case "m":
      {
        const month = parseInt(selectedContent, 10);
        isValidForFormat =
          /^\d{1,2}$/.test(selectedContent) && month >= 1 && month <= 12;
      }
      break;

    case "d":
      {
        const day = parseInt(selectedContent, 10);
        isValidForFormat =
          /^\d{1,2}$/.test(selectedContent) && day >= 1 && day <= 31;
      }
      break;

    case "dm":
      if (partCount === 2 && /^\d{1,2}[./-]\d{1,2}$/.test(selectedContent)) {
        const [day, month] = parts!.map((p) => parseInt(p, 10));
        isValidForFormat = day >= 1 && day <= 31 && month >= 1 && month <= 12;
      }
      break;

    case "md":
      if (partCount === 2 && /^\d{1,2}[./-]\d{1,2}$/.test(selectedContent)) {
        const [month, day] = parts!.map((p) => parseInt(p, 10));
        isValidForFormat = month >= 1 && month <= 12 && day >= 1 && day <= 31;
      }
      break;

    case "ym":
      if (
        partCount === 2 &&
        /^(?:\d{4}[./-]\d{1,2}|\d{1,2}[./-]\d{4})$/.test(selectedContent)
      ) {
        const [part1, part2] = parts!.map((p) => parseInt(p, 10));
        // year-month format
        if (part1 > 31) {
          isValidForFormat = part2 >= 1 && part2 <= 12;
        } else {
          isValidForFormat = false;
        }
      }
      break;

    case "my":
      if (
        partCount === 2 &&
        /^(?:\d{4}[./-]\d{1,2}|\d{1,2}[./-]\d{4})$/.test(selectedContent)
      ) {
        const [part1, part2] = parts!.map((p) => parseInt(p, 10));
        // month-year format
        if (part2 > 31) {
          isValidForFormat = part1 >= 1 && part1 <= 12;
        } else {
          isValidForFormat = false;
        }
      }
      break;

    case "dmy":
      if (
        partCount === 3 &&
        /^\d{1,2}[./-]\d{1,2}[./-]\d{2,4}$/.test(selectedContent)
      ) {
        const [day, month, year] = parts!.map((p) => parseInt(p, 10));
        isValidForFormat =
          day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 0;
      }
      break;

    case "mdy":
      if (
        partCount === 3 &&
        /^\d{1,2}[./-]\d{1,2}[./-]\d{2,4}$/.test(selectedContent)
      ) {
        const [month, day, year] = parts!.map((p) => parseInt(p, 10));
        isValidForFormat =
          month >= 1 && month <= 12 && day >= 1 && day <= 31 && year >= 0;
      }
      break;

    case "ymd":
      if (
        partCount === 3 &&
        /^\d{1,2}[./-]\d{1,2}[./-]\d{2,4}$/.test(selectedContent)
      ) {
        const [year, month, day] = parts!.map((p) => parseInt(p, 10));
        // Year should be reasonable (could be 2-digit or 4-digit)
        isValidForFormat =
          month >= 1 && month <= 12 && day >= 1 && day <= 31 && year >= 0;
      }
      break;

    case "ddmmyyyy":
      if (/^\d{8}$/.test(selectedContent)) {
        const day = parseInt(selectedContent.substring(0, 2), 10);
        const month = parseInt(selectedContent.substring(2, 4), 10);
        isValidForFormat = day >= 1 && day <= 31 && month >= 1 && month <= 12;
      }
      break;

    case "yyyymmdd":
      if (/^\d{8}$/.test(selectedContent)) {
        const month = parseInt(selectedContent.substring(4, 6), 10);
        const day = parseInt(selectedContent.substring(6, 8), 10);
        isValidForFormat = month >= 1 && month <= 12 && day >= 1 && day <= 31;
      }
      break;

    case "ddmm":
      if (/^\d{4}$/.test(selectedContent) && !hasDelimiters) {
        const day = parseInt(selectedContent.substring(0, 2), 10);
        const month = parseInt(selectedContent.substring(2, 4), 10);
        isValidForFormat = day >= 1 && day <= 31 && month >= 1 && month <= 12;
      }
      break;

    case "mmdd":
      if (/^\d{4}$/.test(selectedContent) && !hasDelimiters) {
        const month = parseInt(selectedContent.substring(0, 2), 10);
        const day = parseInt(selectedContent.substring(2, 4), 10);
        isValidForFormat = month >= 1 && month <= 12 && day >= 1 && day <= 31;
      }
      break;

    case "yyyymm":
      if (/^\d{6}$/.test(selectedContent)) {
        const month = parseInt(selectedContent.substring(4, 6), 10);
        isValidForFormat = month >= 1 && month <= 12;
      }
      break;

    case "mmyyyy":
      if (/^\d{6}$/.test(selectedContent)) {
        const month = parseInt(selectedContent.substring(0, 2), 10);
        isValidForFormat = month >= 1 && month <= 12;
      }
      break;

    default:
      isValidForFormat = false;
  }

  if (!isValidForFormat) {
    // Don't add the tag if format doesn't match
    return false;
  }

  const values =
    selectedLanguage === "automatic"
      ? `date, , ${outputFormat}`
      : `date, ${selectedLanguage}, ${outputFormat}`;

  handleSSMLValueClick(
    editorRef,
    SSMLTag.SayAs,
    values,
    SSMLTagsColors.SayAsTag,
    [SSMLTag.SayAs]
  );
  return true;
};

export const handleTimeSelection = (
  outputFormat: string,
  selectedContent: string,
  selectedScriptLanguage: string,
  selectedLanguage: string,
  editorRef: React.RefObject<EditorRef | null>
): boolean => {
  // Prevent errors caused by trailing spaces
  selectedContent = selectedContent.trim();
  let regex: RegExp = /.*/;

  // Accepts: HH:MM, HH:MM:SS, HH.MM (with : . or space as separators)
  if (outputFormat === "hms24") {
    regex =
      /^(?:[01]?[0-9]|2[0-3])(?:[:. ]([0-5][0-9]))?(?:[:. ]([0-5][0-9]))?$/;
  }
  // Accepts: 1-12 hours with optional minutes/seconds and optional AM/PM
  if (outputFormat === "hms12") {
    regex =
      /^(?:0?[1-9]|1[0-2])(?:[:. ]([0-5][0-9]))?(?:[:. ]([0-5][0-9]))?(?: ?(?:AM|A\.M\.|PM|P\.M\.|am|a\.m\.|pm|p\.m\.|A|P))?$/;
  }

  if (outputFormat === "hms12" && selectedScriptLanguage === "de-DE") {
    regex = /^(?:0?[1-9]|1[0-2])(?:[:. ]([0-5][0-9]))?(?:[:. ]([0-5][0-9]))?$/;
  }
  if (selectedContent !== "" && !regex.test(selectedContent)) {
    return false;
  }

  const values =
    selectedLanguage === "automatic"
      ? `time, , ${outputFormat}`
      : `time, ${selectedLanguage}, ${outputFormat}`;

  handleSSMLValueClick(
    editorRef,
    SSMLTag.SayAs,
    values,
    SSMLTagsColors.SayAsTag,
    [SSMLTag.SayAs]
  );

  return true;
};

export const handleNumberSelection = (
  selectedOption: string,
  selectedContent: string,
  selectedLanguage: string,
  editorRef: React.RefObject<EditorRef | null>
): boolean => {
  selectedContent = selectedContent.trim();

  let regex: RegExp = /.*/;

  if (selectedOption === "cardinal" || selectedOption === "decimal") {
    /**
     * ALLOWS:
     *  -10
     *  +10
     *  10%
     *  -10%
     *  10x
     *  1,234
     *  1,234.56
     *  -1,234.56%
     *  3.5x
     */
    regex = /^[+-]?\d+(?:[.,]\d{3})*(?:[.,]\d+)?[%x]?$/i;
  } else if (selectedOption === "ordinal") {
    // Allows: 1st, 2nd, 3rd, 21st, -3rd
    regex = /^-?\d+(st|nd|rd|th)?$/i;
  } else if (selectedOption === "digits") {
    // Allows only digits, + or -
    regex = /^[+-]?\d+$/;
  } else if (selectedOption === "roman") {
    // Standard Roman numerals
    regex = /^(?=[MDCLXVI])M*(C[MD]|D?C*)(X[CL]|L?X*)(I[XV]|V?I*)$/i;
  } else if (selectedOption === "currency") {
    /**
     * ALLOWS:
     *  €44.02
     *  USD33.01
     *  USD 33.01
     *  $100
     *  100 EUR
     *  1,234.56 €
     */
    regex =
      /^(?:(?:€|¥|\$|£|Fr|CAD|SGD|USD|NZD|AUD|HKD|JPY|CHF|EUR|GBP)\s*\d{1,3}(?:[.,]\d{3})*(?:[.,]\d+)?|\d{1,3}(?:[.,]\d{3})*(?:[.,]\d+)?\s*(?:€|¥|\$|£|Fr|CAD|SGD|USD|NZD|AUD|HKD|JPY|CHF|EUR|GBP))$/i;
  }

  if (selectedContent === "" || regex.test(selectedContent)) {
    const values =
      selectedLanguage === "automatic"
        ? `${selectedOption}`
        : `${selectedOption}, ${selectedLanguage}`;

    handleSSMLValueClick(
      editorRef,
      SSMLTag.SayAs,
      values,
      SSMLTagsColors.SayAsTag,
      [SSMLTag.SayAs]
    );
    return true;
  }
  return false;
};

export const handleOrdinalSelection = (
  selectedOption: string,
  selectedContent: string,
  editorRef: React.RefObject<EditorRef | null>,
  selectedLanguage: string
): boolean => {
  let regex = /^-?\d+(st|nd|rd|th)?$/;
  if (selectedLanguage === "de-DE") {
    regex = /^-?\d+(st|nd|rd|th|\.)?$/;
  }

  if (selectedContent === "" || regex.test(selectedContent)) {
    const values =
      selectedLanguage === "automatic"
        ? `${selectedOption}`
        : `${selectedOption}, ${selectedLanguage}`;

    handleSSMLValueClick(
      editorRef,
      SSMLTag.SayAs,
      values,
      SSMLTagsColors.SayAsTag,
      [SSMLTag.SayAs]
    );
    return true;
  }
  return false;
};
